// ROI Calculator Store
// Zustand store for ROI Calculator state management

import { create } from 'zustand'
import type { Region, StackItem, ROICalculation, AggregatedResults, ProductBenchmark } from './types'
import { INDUSTRIES, getProduct } from './data'

interface ROIState {
  // State
  regions: Region[]
  activeRegionId: number | null
  selectedFlowRegionId: number | null
  isCustomerView: boolean
  nextRegionId: number

  // Computed values cache
  calculations: Map<number, ROICalculation>
  aggregatedResults: AggregatedResults | null

  // Actions
  addRegion: (data?: Partial<Region>) => void
  removeRegion: (regionId: number) => void
  updateRegion: (regionId: number, updates: Partial<Region>) => void
  setActiveRegion: (regionId: number | null) => void
  setSelectedFlowRegion: (regionId: number) => void
  updateRegionField: <K extends keyof Region>(regionId: number, field: K, value: Region[K]) => void
  addProductToStack: (regionId: number, product: ProductBenchmark) => void
  removeStackItem: (regionId: number, stackIndex: number) => void
  moveStackItem: (regionId: number, fromIndex: number, toIndex: number) => void
  applyBestStack: (regionId: number) => void
  setCustomerView: (isCustomer: boolean) => void
  reset: () => void
  calculate: () => void
  getRegionCalculation: (regionId: number) => ROICalculation | null
  getROIState: () => { regions: Region[] }
  loadROIState: (state: { regions: Region[] }) => void
}

// Default region values
const getDefaultRegion = (id: number): Region => ({
  id,
  name: 'Australia',
  country: 'Australia',
  industry: '',
  provider: '',
  product: '',
  passRate: 0,
  volume: 0,
  valuePerCustomer: 0,
  costPerCheck: 0,
  stack: [],
})

// Calculate stacked pass rate using cascading model
function calculateStackedPassRate(basePassRate: number, stack: StackItem[]): number {
  if (!stack || stack.length === 0) {
    return basePassRate
  }

  let currentPassRate = basePassRate
  let failRate = 100 - basePassRate

  stack.forEach((item) => {
    // Each product catches a portion of the remaining failures
    const additionalPasses = failRate * (item.incrementalLift / 100)
    currentPassRate += additionalPasses
    failRate -= additionalPasses
  })

  return Math.min(currentPassRate, 99.9) // Cap at 99.9%
}

// Calculate ROI for a single region
function calculateRegionROI(region: Region): ROICalculation {
  const newRate = calculateStackedPassRate(region.passRate, region.stack)
  const improvement = newRate - region.passRate
  const currentConversions = region.volume * (region.passRate / 100)
  const projectedConversions = region.volume * (newRate / 100)
  const additionalConversions = projectedConversions - currentConversions
  const monthlyValue = additionalConversions * region.valuePerCustomer
  const annualValue = monthlyValue * 12

  return {
    newRate,
    improvement,
    currentConversions,
    projectedConversions,
    additionalConversions,
    monthlyValue,
    annualValue,
  }
}

// Get best stack for a region based on country
function getBestStackForRegion(region: Region): StackItem[] {
  const priorityProfiles = ['default', 'safe_harbour_id', 'gov_id']

  // Country-specific products first
  const countrySpecific: string[] =
    region.country === 'Australia'
      ? ['au-all-sources', 'dvs_bio']
      : region.country === 'New Zealand'
        ? ['nz-all-sources']
        : []

  const selected: StackItem[] = []

  // Add country-specific products
  countrySpecific.forEach((profile) => {
    if (selected.length < 3) {
      const product = getProduct(profile)
      if (product) {
        selected.push({
          profile: product.profile,
          description: product.description,
          passRate: product.passRate,
          incrementalLift: product.incrementalLift,
        })
      }
    }
  })

  // Add priority products (up to 3 total)
  priorityProfiles.forEach((profile) => {
    if (selected.length < 3 && !selected.find((p) => p.profile === profile)) {
      const product = getProduct(profile)
      if (product) {
        selected.push({
          profile: product.profile,
          description: product.description,
          passRate: product.passRate,
          incrementalLift: product.incrementalLift,
        })
      }
    }
  })

  // Sort by lift (highest first)
  selected.sort((a, b) => b.incrementalLift - a.incrementalLift)

  return selected.slice(0, 3)
}

export const useROIStore = create<ROIState>((set, get) => ({
  // Initial state
  regions: [getDefaultRegion(1)],
  activeRegionId: 1,
  selectedFlowRegionId: 1,
  isCustomerView: false,
  nextRegionId: 2,
  calculations: new Map(),
  aggregatedResults: null,

  // Add a new region
  addRegion: (data) => {
    const { nextRegionId } = get()
    const newRegion: Region = {
      ...getDefaultRegion(nextRegionId),
      ...data,
      id: nextRegionId,
    }

    set((state) => ({
      regions: [...state.regions, newRegion],
      activeRegionId: nextRegionId,
      selectedFlowRegionId: nextRegionId,
      nextRegionId: nextRegionId + 1,
    }))
    get().calculate()
  },

  // Remove a region
  removeRegion: (regionId) => {
    const { regions } = get()
    if (regions.length <= 1) return // Keep at least one region

    const newRegions = regions.filter((r) => r.id !== regionId)
    const newActiveId = newRegions[0]?.id ?? null

    set({
      regions: newRegions,
      activeRegionId: newActiveId,
      selectedFlowRegionId: newActiveId,
    })
    get().calculate()
  },

  // Update region with partial data
  updateRegion: (regionId, updates) => {
    set((state) => ({
      regions: state.regions.map((r) => (r.id === regionId ? { ...r, ...updates } : r)),
    }))
    get().calculate()
  },

  // Set active (expanded) region
  setActiveRegion: (regionId) => {
    const { activeRegionId } = get()
    // Toggle collapse if clicking same region
    const newActiveId = activeRegionId === regionId ? null : regionId

    set({
      activeRegionId: newActiveId,
      selectedFlowRegionId: regionId ?? get().selectedFlowRegionId,
    })
  },

  // Set which region to show in flow visualization
  setSelectedFlowRegion: (regionId) => {
    set({ selectedFlowRegionId: regionId })
  },

  // Update a single field on a region
  updateRegionField: (regionId, field, value) => {
    set((state) => ({
      regions: state.regions.map((r) => {
        if (r.id !== regionId) return r

        const updated = { ...r, [field]: value }

        // Auto-fill valuePerCustomer when industry changes
        if (field === 'industry') {
          const industry = INDUSTRIES.find((i) => i.id === value)
          if (industry && industry.arpu.monthly > 0) {
            updated.valuePerCustomer = industry.arpu.monthly
          }
        }

        return updated
      }),
    }))
    get().calculate()
  },

  // Add product to stack
  addProductToStack: (regionId, product) => {
    set((state) => ({
      regions: state.regions.map((r) => {
        if (r.id !== regionId) return r
        // Don't add duplicates
        if (r.stack.find((s) => s.profile === product.profile)) return r

        return {
          ...r,
          stack: [
            ...r.stack,
            {
              profile: product.profile,
              description: product.description,
              passRate: product.passRate,
              incrementalLift: product.incrementalLift,
            },
          ],
        }
      }),
    }))
    get().calculate()
  },

  // Remove item from stack
  removeStackItem: (regionId, stackIndex) => {
    set((state) => ({
      regions: state.regions.map((r) => {
        if (r.id !== regionId) return r
        return {
          ...r,
          stack: r.stack.filter((_, i) => i !== stackIndex),
        }
      }),
    }))
    get().calculate()
  },

  // Move stack item (reorder)
  moveStackItem: (regionId, fromIndex, toIndex) => {
    set((state) => ({
      regions: state.regions.map((r) => {
        if (r.id !== regionId) return r
        const newStack = [...r.stack]
        const [removed] = newStack.splice(fromIndex, 1)
        newStack.splice(toIndex, 0, removed)
        return { ...r, stack: newStack }
      }),
    }))
    get().calculate()
  },

  // Apply best stack automatically
  applyBestStack: (regionId) => {
    const region = get().regions.find((r) => r.id === regionId)
    if (!region) return

    const bestStack = getBestStackForRegion(region)

    set((state) => ({
      regions: state.regions.map((r) => (r.id === regionId ? { ...r, stack: bestStack } : r)),
    }))
    get().calculate()
  },

  // Set customer view mode
  setCustomerView: (isCustomer) => {
    set({ isCustomerView: isCustomer })
  },

  // Reset to initial state
  reset: () => {
    set({
      regions: [getDefaultRegion(1)],
      activeRegionId: 1,
      selectedFlowRegionId: 1,
      isCustomerView: false,
      nextRegionId: 2,
      calculations: new Map(),
      aggregatedResults: null,
    })
  },

  // Recalculate all ROI values
  calculate: () => {
    const { regions } = get()
    const calculations = new Map<number, ROICalculation>()

    let totalCurrentConversions = 0
    let totalProjectedConversions = 0
    let totalVolume = 0
    let totalMonthlyValue = 0
    let weightedImprovement = 0

    regions.forEach((region) => {
      const calc = calculateRegionROI(region)
      calculations.set(region.id, calc)

      totalCurrentConversions += calc.currentConversions
      totalProjectedConversions += calc.projectedConversions
      totalVolume += region.volume
      totalMonthlyValue += calc.monthlyValue
      weightedImprovement += calc.improvement * region.volume
    })

    const avgImprovement = totalVolume > 0 ? weightedImprovement / totalVolume : 0

    const aggregatedResults: AggregatedResults = {
      totalCurrentConversions,
      totalProjectedConversions,
      totalVolume,
      totalMonthlyValue,
      totalAnnualValue: totalMonthlyValue * 12,
      avgImprovement,
      totalAdditionalConversions: totalProjectedConversions - totalCurrentConversions,
    }

    set({ calculations, aggregatedResults })
  },

  // Get calculation for a specific region
  getRegionCalculation: (regionId) => {
    return get().calculations.get(regionId) ?? null
  },

  // Get serializable state for saving to DB
  getROIState: () => {
    const { regions } = get()
    return { regions }
  },

  // Load state from DB and recalculate
  loadROIState: (state) => {
    if (state.regions && state.regions.length > 0) {
      const maxId = Math.max(...state.regions.map(r => r.id))
      set({
        regions: state.regions,
        activeRegionId: state.regions[0].id,
        selectedFlowRegionId: state.regions[0].id,
        nextRegionId: maxId + 1,
      })
      get().calculate()
    }
  },
}))

